<template>
  <section class="chat-panel">
    <div v-if="!selectedDialog" class="chat-panel__empty">
      <div class="chat-panel__empty-card">
        <img src="/img/chat.svg" alt="" class="chat-panel__empty-image" />
        <div class="chat-panel__empty-title">Диалог не выбран</div>
        <p>Выберите или создайте диалог для начала общения</p>
      </div>
    </div>

    <template v-else>
      <header class="chat-panel__header">
        <div class="chat-panel__header-main">
          <a
            v-if="selectedDialog.client_phone"
            :href="`tel:${selectedDialog.client_phone}`"
            class="chat-panel__header-icon"
            aria-label="Позвонить клиенту"
          >
            <el-icon><PhoneFilled /></el-icon>
          </a>
          <span v-else class="chat-panel__header-icon chat-panel__header-icon--static">
            <el-icon><PhoneFilled /></el-icon>
          </span>

          <div class="chat-panel__header-meta">
            <div class="chat-panel__title">{{ selectedDialog.client_name || 'Диалог' }}</div>
            <div class="chat-panel__subtitle">{{ headerStatusText }}</div>
          </div>
        </div>

        <button type="button" class="chat-panel__header-action" aria-label="Редактировать диалог">
          <el-icon><EditPen /></el-icon>
        </button>
      </header>

      <div class="chat-panel__messages message-block" @scroll.passive="handleScroll" ref="chatContainer">
        <div v-for="(group, index) in groupedMessages" :key="index" class="chat-group">
          <div v-if="group.type === 'date'" class="chat-date-chip">{{ group.date }}</div>

          <div
            v-else
            class="chat-message-row"
            :class="group.isMine ? 'chat-message-row--mine' : 'chat-message-row--other'"
          >
            <div
              class="chat-message-stack"
              :class="group.isMine ? 'chat-message-stack--mine' : 'chat-message-stack--other'"
            >
              <div
                v-for="message in group.messages"
                :key="message.id"
                :data-message-id="message.id"
                class="chat-message-item"
              >
                <template v-if="shouldShowContent(message)">
                  <div
                    v-if="isHtmlAllowed()"
                    v-html="message.content"
                    class="chat-message-text"
                  ></div>
                  <div v-else class="chat-message-text">{{ message.content }}</div>
                </template>

                <div v-if="fileUrl(message)" class="chat-attachments">
                  <template v-if="isImageUrl(fileUrl(message))">
                    <el-image
                      :src="fileUrl(message)"
                      :preview-src-list="[fileUrl(message)]"
                      fit="cover"
                      class="chat-attachment-image"
                    />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isVideoUrl(fileUrl(message), message)">
                    <video :src="fileUrl(message)" controls class="chat-attachment-video" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else-if="isAudioUrl(fileUrl(message), message)">
                    <audio :src="fileUrl(message)" controls class="chat-attachment-audio" />
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>Скачать</span>
                    </a>
                  </template>

                  <template v-else>
                    <a :href="fileUrl(message)" :download="attachmentName(message)" class="chat-attachment-link">
                      <el-icon><Download /></el-icon>
                      <span>{{ attachmentName(message) }}</span>
                    </a>
                  </template>
                </div>

                <div class="chat-message-meta">{{ formatBubbleTime(message.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer class="chat-panel__composer">
        <el-upload
          ref="uploadRef"
          class="chat-panel__upload"
          :auto-upload="false"
          :action="uploadUrl"
          :data="{ client_id: selectedDialog?.client_id }"
          :show-file-list="false"
          :on-remove="handleUploadRemove"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :headers="{ Authorization: 'Bearer ' + getToken() }"
          v-model:file-list="uploadFiles"
        />

        <div v-if="uploadFiles.length" class="chat-panel__files">
          <div v-for="file in uploadFiles" :key="file.uid" class="chat-panel__file">
            <span class="chat-panel__file-name">{{ file.name }}</span>
            <el-progress
              v-if="typeof file.percentage === 'number'"
              :percentage="Math.round(file.percentage)"
              :stroke-width="6"
              :status="file.status === 'success' ? 'success' : (file.status === 'fail' ? 'exception' : undefined)"
              class="chat-panel__file-progress"
            />
            <span v-else class="chat-panel__file-ready">Готов к отправке</span>
            <button type="button" class="chat-panel__file-remove" @click="removeFile(file)">
              <el-icon><Close /></el-icon>
            </button>
          </div>
        </div>

        <div class="chat-panel__composer-shell">
          <QuillEditor
            ref="editor"
            v-model:content="messageHtml"
            contentType="html"
            theme="snow"
            :toolbar="[]"
            placeholder="Сообщение..."
            class="chat-editor"
            @keydown.enter.exact.prevent="sendMessage"
          />

          <div class="chat-panel__composer-actions">
            <button type="button" class="chat-panel__icon-btn" @click="triggerFileSelect">
              <el-icon><Paperclip /></el-icon>
            </button>
            <button
              type="button"
              class="chat-panel__icon-btn chat-panel__icon-btn--primary"
              @click="sendMessage"
              :disabled="!canSendMessage"
            >
              <el-icon><Microphone /></el-icon>
            </button>
          </div>
        </div>
      </footer>
    </template>
  </section>
</template>

<script>
import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { mapState } from 'vuex'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { Close, Download, EditPen, Microphone, Paperclip, PhoneFilled } from '@element-plus/icons-vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'ChatPanel',
  components: {
    Close,
    Download,
    EditPen,
    Microphone,
    Paperclip,
    PhoneFilled,
    QuillEditor
  },
  props: {
    selectedDialog: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loadingOlder: false,
      messageHtml: '',
      page: 1,
      pageSize: 25,
      seenFlushTimeout: null,
      observerInitialized: false,
      uploadUrl: this.$store.getters['messengers/getUploadUrl'],
      uploadFiles: [],
      pendingFiles: [],
      isSending: false,
      sendFileUrl: 'https://lk.lineon.me/api/messages.sendFile'
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('messengers', ['messages', 'messengers']),
    ...mapState('socket', ['waAuthCheckInProgress', 'whatsappReadyStatuses']),
    currentMessenger() {
      return this.messengers.find(messenger => Number(messenger.id) === Number(this.selectedDialog?.messenger_id)) || null
    },
    headerStatusText() {
      const rawDate = this.selectedDialog?.last_active
      if (!rawDate) return 'был(а) недавно'

      const timezoneName = this.user?.timezone || 'Europe/Moscow'
      const parsed = dayjs(String(rawDate).replace(' ', 'T')).tz(timezoneName)
      if (!parsed.isValid()) return 'был(а) недавно'

      const now = dayjs().tz(timezoneName)
      const minutesDiff = Math.abs(now.diff(parsed, 'minute'))
      if (minutesDiff <= 30) return 'был(а) недавно'
      if (parsed.isSame(now, 'day')) return `в сети ${parsed.format('HH:mm')}`
      if (parsed.isSame(now.subtract(1, 'day'), 'day')) return 'был(а) вчера'
      return `был(а) ${parsed.format('DD.MM.YYYY')}`
    },
    canSendMessage() {
      return !this.isSending && (this.hasMeaningfulHtml(this.messageHtml) || this.pendingFiles.length > 0)
    },
    sortedMessages() {
      const dialogId = this.selectedDialog?.id
      if (!dialogId) return []
      return this.messages
        .filter(message => String(message.dialog_id) === String(dialogId))
        .map(message => ({
          ...message,
          id: Number(message.id),
          dialog_id: Number(message.dialog_id),
          sender_user_id: message.sender_user_id ? Number(message.sender_user_id) : null,
          created_at: new Date(Number(message.created_at) * 1000),
          updated_at: message.updated_at ? new Date(Number(message.updated_at) * 1000) : null,
          attachments: message.attachments
            ? (typeof message.attachments === 'string'
              ? (() => {
                try {
                  return JSON.parse(message.attachments)
                } catch {
                  return null
                }
              })()
              : message.attachments)
            : null,
          sender_name: message.sender_name || ''
        }))
        .sort((first, second) => first.created_at - second.created_at)
    },
    groupedMessages() {
      const groups = []
      let currentGroup = null
      let lastDate = null

      for (const message of this.sortedMessages) {
        const messageDate = dayjs(message.created_at).format('DD.MM.YYYY')

        if (messageDate !== lastDate) {
          groups.push({ type: 'date', date: messageDate })
          lastDate = messageDate
        }

        const timestamp = message.created_at.getTime()
        if (
          currentGroup &&
          currentGroup.type === 'messages' &&
          currentGroup.sender_type === message.sender_type &&
          currentGroup.sender_user_id === message.sender_user_id &&
          Math.abs(timestamp - currentGroup.lastTimestamp) <= 60000
        ) {
          currentGroup.messages.push(message)
          currentGroup.lastTimestamp = timestamp
        } else {
          if (currentGroup) groups.push(currentGroup)
          currentGroup = {
            type: 'messages',
            sender_type: message.sender_type,
            sender_user_id: message.sender_user_id,
            isMine: message.sender_type === 'manager',
            messages: [message],
            lastTimestamp: timestamp
          }
        }
      }

      if (currentGroup) groups.push(currentGroup)
      return groups
    }
  },
  watch: {
    selectedDialog: {
      immediate: true,
      handler(newVal, oldVal) {
        try {
          const token = this.$cookies.get('token') || (this.$store.state && this.$store.state.cookies && this.$store.state.cookies.get && this.$store.state.cookies.get('token'))
          if (token) {
            const decodeJwt = (typeof window !== 'undefined' && window.decodeJwt) ? window.decodeJwt : (require && require('jwt-decode'))
            const decoded = typeof decodeJwt === 'function' ? decodeJwt(token) : decodeJwt.default(token)
            const accountId = decoded.account_id
            if (oldVal && oldVal.id) {
              const oldChannel = `new-message_${accountId}_${oldVal.id}`
              if (this.$store.state.socket && this.$store.state.socket.socket && this.$store.state.socket.socket.off) {
                this.$store.state.socket.socket.off(oldChannel)
              }
            }
          }
        } catch (error) {
          console.error(error)
        }

        this.page = 1
        this.observerInitialized = false
        this.pendingFiles = []
        this.uploadFiles = []

        if (!newVal) {
          this.messageHtml = ''
        }

        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },
    messages() {
      if (!this.loadingOlder) {
        this.scrollToBottom()
      }
    },
    sortedMessages(newValue) {
      if (newValue.length && this.observer) {
        this.observer.disconnect()
      }

      if (newValue.length && !this.observerInitialized) {
        this.observerInitialized = true
        this.$nextTick(() => {
          const container = this.$refs.chatContainer
          if (!container || !this.observer) return
          const allElements = container.querySelectorAll('[data-message-id]')
          allElements.forEach(element => {
            this.observer.observe(element)
          })
        })
      }
    }
  },
  methods: {
    triggerFileSelect() {
      const upload = this.$refs.uploadRef
      if (upload && upload.$el) {
        const input = upload.$el.querySelector('input[type=file]')
        if (input) input.click()
      }
    },
    getToken() {
      return document.cookie.split('; ').find(row => row.startsWith('token='))?.split('=')[1] || ''
    },
    handleUploadRemove(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    removeFile(file) {
      this.uploadFiles = this.uploadFiles.filter(item => item.uid !== file.uid)
      this.pendingFiles = this.pendingFiles.filter(item => item.uid !== file.uid)
    },
    formatDate(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      const now = dayjs().tz(accountTimezone)
      const yesterday = now.subtract(1, 'day')

      if (local.isSame(now, 'day')) {
        return `Сегодня ${local.format('HH:mm')}`
      }
      if (local.isSame(yesterday, 'day')) {
        return `Вчера ${local.format('HH:mm')}`
      }
      return local.isValid() ? local.format('DD.MM.YYYY HH:mm') : ''
    },
    formatBubbleTime(date) {
      if (!date) return ''
      const accountTimezone = this.user.timezone || 'Europe/Moscow'
      const local = dayjs(date).tz(accountTimezone)
      return local.isValid() ? local.format('HH:mm') : ''
    },
    hasMeaningfulHtml(html) {
      return String(html || '')
        .replace(/<p><br><\/p>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<br\s*\/?>(?=\s*$)/gi, '')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0
    },
    async handleScroll() {
      const container = this.$refs.chatContainer
      if (!container || container.scrollTop !== 0 || this.loadingOlder) return

      const previousHeight = container.scrollHeight
      this.loadingOlder = true
      this.page += 1

      await this.$store.dispatch('messengers/fetchMessages', {
        dialog_id: this.selectedDialog.id,
        page: this.page,
        pageSize: this.pageSize
      })

      this.loadingOlder = false
      this.$nextTick(() => {
        const newHeight = container.scrollHeight
        container.scrollTop = newHeight - previousHeight
      })
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer
        if (!container) return
        container.scrollTop = container.scrollHeight
      })
    },
    isHtmlAllowed() {
      return ['email', 'telegram', 'whatsapp'].includes(this.currentMessenger?.type)
    },
    async sendMessage() {
      const html = String(this.messageHtml || '').trim()
      if (!this.hasMeaningfulHtml(html) && this.pendingFiles.length === 0) return

      const messenger = this.currentMessenger
      if (!messenger) return

      if (messenger.type === 'whatsapp') {
        this.isSending = true
        try {
          if (this.pendingFiles.length > 0) {
            const rawHtml = html
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('caption', index === 0 ? rawHtml : '')
              formData.append('content', rawHtml)
              formData.append('content_raw', rawHtml)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_phone))
              formData.append('client_phone', this.selectedDialog.client_phone)
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }

            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-whatsapp-message',
              data: {
                messenger_id: messenger.id,
                client_id: Number(this.selectedDialog.client_phone),
                dialog_id: Number(this.selectedDialog.id),
                content: html
              }
            })
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'email') {
        this.isSending = true
        try {
          const formData = new FormData()
          formData.append('dialog_id', Number(this.selectedDialog.id))
          formData.append('text', html)
          for (const file of this.pendingFiles) {
            formData.append('files[]', file.raw)
          }

          await axios.post('https://lk.lineon.me/api/email.send', formData, {
            headers: { Authorization: 'Bearer ' + this.getToken() }
          })

          this.pendingFiles = []
          this.uploadFiles = []
          this.messageHtml = ''
          if (this.$refs.editor && this.$refs.editor.setHTML) {
            this.$refs.editor.setHTML('')
          }
          this.scrollToBottom()
        } finally {
          this.isSending = false
        }
      } else if (messenger.type === 'telegram') {
        this.isSending = true
        try {
          const text = html
          if (!this.hasMeaningfulHtml(text) && this.pendingFiles.length === 0) return

          if (this.pendingFiles.length > 0) {
            for (let index = 0; index < this.pendingFiles.length; index += 1) {
              const file = this.pendingFiles[index]
              const formData = new FormData()
              formData.append('dialog_id', Number(this.selectedDialog.id))
              formData.append('files[]', file.raw)
              formData.append('messenger_id', this.selectedDialog.messenger_id)
              formData.append('client_id', Number(this.selectedDialog.client_id))
              formData.append('caption', index === 0 ? text : '')
              formData.append('messenger_type', messenger.type)

              file.status = 'uploading'
              file.percentage = 0
              try {
                await axios.post(this.sendFileUrl, formData, {
                  headers: { Authorization: 'Bearer ' + this.getToken() },
                  onUploadProgress: event => {
                    if (event.total) file.percentage = Math.round((event.loaded / event.total) * 100)
                  }
                })
                file.status = 'success'
              } catch (error) {
                console.error('sendFile error', error)
                file.status = 'fail'
              }
              await new Promise(resolve => setTimeout(resolve, 300))
            }
            this.pendingFiles = []
            this.uploadFiles = []
            this.messageHtml = ''
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          } else {
            this.$store.dispatch('socket/send', {
              event: 'send-telegram-message',
              data: {
                messenger_id: messenger.id,
                dialog_id: Number(this.selectedDialog.id),
                client_id: Number(this.selectedDialog.client_id),
                content: text
              }
            })
            this.messageHtml = ''
            this.uploadFiles = []
            this.pendingFiles = []
            if (this.$refs.editor && this.$refs.editor.setHTML) {
              this.$refs.editor.setHTML('')
            }
            this.scrollToBottom()
          }
        } finally {
          this.isSending = false
        }
      }
    },
    beforeUpload() {
      if (!this.selectedDialog || !this.selectedDialog.client_id) {
        this.$message.warning('Выберите диалог перед загрузкой файла')
        return false
      }
      return false
    },
    handleFileChange(file, fileList) {
      this.uploadFiles = fileList
      this.pendingFiles = fileList.map(item => item)
    },
    fileUrl(message) {
      if (message && typeof message.file_url === 'string' && message.file_url) return message.file_url
      const attachments = message && message.attachments
      if (!attachments) return ''
      if (typeof attachments === 'string') return attachments
      if (Array.isArray(attachments) && attachments.length) {
        const firstAttachment = attachments[0]
        if (typeof firstAttachment === 'string') return firstAttachment
        if (firstAttachment && typeof firstAttachment === 'object' && firstAttachment.url) return firstAttachment.url
      }
      if (typeof attachments === 'object' && attachments.url) return attachments.url
      return ''
    },
    isImageUrl(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url || '')
    },
    isVideoUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('video/')
      const extension = (url || '').toLowerCase()
      return extension.endsWith('.mp4') || extension.endsWith('.webm')
    },
    isAudioUrl(url, message) {
      const mime = (message?.mimetype || '').toLowerCase()
      if (mime) return mime.startsWith('audio/')
      const extension = (url || '').toLowerCase()
      return /\.(mp3|m4a|aac|ogg|oga|wav|opus)$/i.test(extension)
    },
    attachmentName(message) {
      const url = this.fileUrl(message) || ''
      try {
        const lastChunk = url.split('/').pop() || ''
        const decoded = decodeURIComponent(lastChunk)
        return decoded || 'файл'
      } catch {
        return 'файл'
      }
    },
    shouldShowContent(message) {
      const text = (message && message.content ? String(message.content) : '').trim()
      if (!text) return false
      const url = this.fileUrl(message)
      if (!url) return true
      try {
        const name = this.attachmentName(message).trim()
        return text !== name
      } catch {
        return true
      }
    }
  },
  mounted() {
    this.seenMessages = new Set()
    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        const messageId = entry.target.getAttribute('data-message-id')
        const message = this.sortedMessages.find(item => String(item.id) === String(messageId))
        if (message && !message.read_at && message.sender_type !== 'manager') {
          this.seenMessages.add(message.id)
          if (this.seenFlushTimeout) {
            clearTimeout(this.seenFlushTimeout)
          }
          this.seenFlushTimeout = setTimeout(() => {
            const ids = Array.from(this.seenMessages)
            if (ids.length) {
              this.seenMessages.clear()
              this.$store.dispatch('messengers/markAsReadBatch', {
                message_ids: ids,
                dialog_id: Number(this.selectedDialog.id),
                messenger_type: this.selectedDialog.messenger_type
              })
            }
            this.seenFlushTimeout = null
          }, 500)
        }
      })
    }, { threshold: 0.5 })

    this.$nextTick(() => {
      setTimeout(() => {
        const container = this.$refs.chatContainer
        if (!container || !this.observer) return
        const allElements = container.querySelectorAll('[data-message-id]')
        allElements.forEach(element => {
          this.observer.observe(element)
        })
        this.scrollToBottom()
      }, 50)
    })
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style scoped>
.chat-panel {
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-panel__empty {
  min-height: 0;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__empty-card {
  width: min(420px, calc(100% - 32px));
  padding: 28px 24px;
  text-align: center;
  border-radius: 16px;
  background: rgba(8, 22, 48, 0.66);
  box-shadow: inset 0 0 0 1px rgba(66, 110, 187, 0.32);
}

.chat-panel__empty-image {
  width: 92px;
  height: 92px;
  object-fit: contain;
  opacity: 0.45;
}

.chat-panel__empty-title {
  margin-top: 14px;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 30px;
}

.chat-panel__empty-card p {
  margin: 10px 0 0;
  color: #9ec4ff;
  font-family: "Oswald", sans-serif;
  font-size: 18px;
}

.chat-panel__header {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 18px;
  border-radius: 12px;
  background: linear-gradient(180deg, #184082 0%, #123261 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__header-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.chat-panel__header-icon,
.chat-panel__header-action {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #0c224b;
  box-shadow: inset 0 0 0 1px rgba(44, 88, 155, 0.6);
  color: #00c3fe;
  text-decoration: none;
  border: none;
}

.chat-panel__header-icon .el-icon,
.chat-panel__header-action .el-icon {
  font-size: 26px;
}

.chat-panel__header-action {
  flex-shrink: 0;
}

.chat-panel__header-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chat-panel__title {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 28px;
  line-height: 1.05;
}

.chat-panel__subtitle {
  color: #c5dbff;
  font-family: "Oswald", sans-serif;
  font-size: 16px;
}

.chat-panel__messages {
  min-height: 0;
  flex: 1;
  overflow: auto;
  padding: 18px 18px 12px;
  border-radius: 14px;
  background-image:
    linear-gradient(180deg, rgba(7, 21, 47, 0.92) 0%, rgba(7, 18, 40, 0.94) 100%),
    radial-gradient(circle at 18px 18px, rgba(32, 79, 152, 0.2) 0 2px, transparent 2.4px),
    radial-gradient(circle at 66px 38px, rgba(32, 79, 152, 0.16) 0 1.8px, transparent 2.2px),
    linear-gradient(135deg, transparent 0 46%, rgba(26, 65, 128, 0.18) 46% 50%, transparent 50% 100%),
    linear-gradient(45deg, transparent 0 47%, rgba(19, 52, 108, 0.14) 47% 51%, transparent 51% 100%);
  background-repeat: no-repeat, repeat, repeat, repeat, repeat;
  background-size: auto, 94px 94px, 128px 128px, 120px 120px, 160px 160px;
  background-position: 0 0, 0 0, 14px 22px, 0 0, 26px 28px;
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-group + .chat-group {
  margin-top: 12px;
}

.chat-date-chip {
  width: fit-content;
  margin: 0 auto 16px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(16, 41, 85, 0.84);
  color: #9dc8ff;
  font-family: "Oswald", sans-serif;
  font-size: 14px;
}

.chat-message-row {
  display: flex;
  margin-bottom: 14px;
}

.chat-message-row--mine {
  justify-content: flex-end;
}

.chat-message-row--other {
  justify-content: flex-start;
}

.chat-message-stack {
  max-width: min(78%, 860px);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-message-stack--mine .chat-message-item {
  border-top-right-radius: 10px;
  background: linear-gradient(180deg, #3f67b0 0%, #284f96 100%);
}

.chat-message-stack--other .chat-message-item {
  border-top-left-radius: 10px;
  background: linear-gradient(180deg, #436db9 0%, #2f5aa5 100%);
}

.chat-message-item {
  min-width: 180px;
  padding: 12px 14px 10px;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(2, 10, 24, 0.12);
}

.chat-message-text {
  color: #ffffff;
  font-family: "Oswald", sans-serif;
  font-size: 17px;
  line-height: 1.35;
  word-break: break-word;
}

.chat-message-meta {
  margin-top: 6px;
  text-align: right;
  color: #86a8df;
  font-family: "Oswald", sans-serif;
  font-size: 12px;
}

.chat-attachments {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-attachment-image,
.chat-attachment-video {
  width: min(280px, 100%);
  max-width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.chat-attachment-audio {
  width: min(320px, 100%);
  max-width: 100%;
}

.chat-attachment-link {
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #c5e7ff;
  text-decoration: none;
  font-family: "Oswald", sans-serif;
  font-size: 14px;
}

.chat-panel__composer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-panel__upload {
  display: none;
}

.chat-panel__files {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-panel__file {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(160px, 220px) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__file-name,
.chat-panel__file-ready {
  min-width: 0;
  color: #d7e8ff;
  font-family: "Oswald", sans-serif;
  font-size: 15px;
}

.chat-panel__file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-panel__file-ready {
  color: #8de5a5;
}

.chat-panel__file-remove {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: #0c224b;
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
  color: #ffffff;
}

.chat-panel__composer-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: stretch;
  padding: 12px 16px;
  border-radius: 12px;
  background: linear-gradient(180deg, #17366b 0%, #112954 100%);
  box-shadow: inset 0 0 0 1px rgba(52, 96, 167, 0.45);
}

.chat-panel__composer-shell::before {
  content: '';
  width: 3px;
  border-radius: 999px;
  background: #00c3fe;
  position: absolute;
  opacity: 0;
}

.chat-panel__composer-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chat-panel__icon-btn {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #00c3fe;
}

.chat-panel__icon-btn .el-icon {
  font-size: 28px;
}

.chat-panel__icon-btn--primary {
  background: transparent;
}

.chat-panel__icon-btn:disabled {
  opacity: 0.4;
}

.chat-editor {
  min-width: 0;
}

.chat-editor :deep(.ql-toolbar) {
  display: none;
}

.chat-editor :deep(.ql-container) {
  border: none !important;
  font-family: "Oswald", sans-serif;
  background: transparent;
}

.chat-editor :deep(.ql-editor) {
  min-height: 44px;
  padding: 2px 0;
  color: #00c3fe;
  font-size: 18px;
  line-height: 1.35;
}

.chat-editor :deep(.ql-editor.ql-blank::before) {
  left: 0;
  right: auto;
  color: #00c3fe;
  font-style: normal;
}

.chat-editor :deep(.ql-editor p) {
  margin: 0;
}

.chat-message-text :deep(p) {
  margin: 0;
}

.chat-message-text :deep(a) {
  color: #d8f1ff;
}

.chat-panel__file-progress :deep(.el-progress-bar__outer) {
  background: rgba(255, 255, 255, 0.12) !important;
}

@media (max-width: 1180px) {
  .chat-panel__title {
    font-size: 24px;
  }

  .chat-message-stack {
    max-width: 86%;
  }
}

@media (max-width: 900px) {
  .chat-panel__header {
    padding: 10px 14px;
  }

  .chat-panel__title {
    font-size: 22px;
  }

  .chat-panel__messages {
    padding: 14px 12px 10px;
  }

  .chat-panel__file {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .chat-panel__header {
    gap: 10px;
  }

  .chat-panel__header-main {
    justify-content: flex-start;
    gap: 10px;
  }

  .chat-panel__header-meta {
    align-items: flex-start;
  }

  .chat-panel__header-icon,
  .chat-panel__header-action {
    width: 42px;
    height: 42px;
  }

  .chat-panel__header-icon .el-icon,
  .chat-panel__header-action .el-icon,
  .chat-panel__icon-btn .el-icon {
    font-size: 24px;
  }

  .chat-panel__title {
    font-size: 18px;
  }

  .chat-panel__subtitle {
    font-size: 14px;
  }

  .chat-message-stack {
    max-width: 100%;
  }

  .chat-message-item {
    min-width: 0;
  }

  .chat-message-text {
    font-size: 15px;
  }

  .chat-panel__composer-shell {
    grid-template-columns: 1fr;
    padding: 10px 12px;
  }

  .chat-panel__composer-actions {
    justify-content: flex-end;
  }
}
</style>
